import React from 'react'
import { Stack } from 'react-bootstrap'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import size from 'lodash/size'
import take from 'lodash/take'
import type { ReactNode } from 'react'
import { LabelValueBlock } from '../Block/LabelValueBlock'
import { LoadingOverlay } from '../loading/loading-overlay'
import { SpinnerSmall } from '../Spinner'
import { ShowIf } from '../ShowIf'
import { HorizontalStack } from '../HorizontalStack'

const ENTITY_PREVIEW_LIST_LIMIT = 10

export type EntityPreviewRow = { label: string; value: ReactNode }
export type EntityPreviewList = { label: string; items: ReactNode[] }
export type EntityPreviewProps = {
  code: string
  statusBadge: ReactNode
  loading: boolean
  rows: EntityPreviewRow[]
  title?: ReactNode
  subtitle?: ReactNode
  list?: EntityPreviewList
}

/** The one 320px hover-preview layout shared by every related-record type — badge, code, title/subtitle, rows, and a capped list.
 * `loading ||` on the title and list blocks: `TooltipPopOver`'s popper computes its position once,
 * the moment the popover first shows — before the query resolves, while `title`/`list` are still
 * blank. Both blocks are ALWAYS supplied by every caller (never structurally absent — `list` only
 * varies in whether its `items` are populated yet), so showing them from the very first (loading)
 * render reserves their height up front; the popover's overall size then never changes once the
 * data arrives, so that one position computation stays correct. An EMPTY `fw-bold fs-6`
 * div measures 0px, not the ~18px it'll be once `title` fills in — a plain space collapses the
 * same way, so `title ?? ' '` uses a real non-breaking space, keeping the line's real height
 * present from that first render too. Reserving height still
 * isn't exact — the `list` block's actual item ROWS only exist once `list.items` is populated, so
 * a record with several items still grows a few px taller than its loading skeleton; `EntityCodeLink`'s
 * `updateOnResize` popper modifier (a `ResizeObserver` on the popper element) catches that
 * remaining growth and repositions, local to that one popover instance. */
export const EntityPreview = ({ code, statusBadge, loading, rows, title, subtitle, list }: EntityPreviewProps) => (
  <LoadingOverlay loading={loading} loadingComponent={SpinnerSmall}>
    <Stack gap={1}>
      <HorizontalStack gap={2}>
        {statusBadge}
        <b>{code}</b>
      </HorizontalStack>
      <ShowIf condition={loading || !!title}>
        <div>
          <div className="fw-bold fs-6 text-truncate">{title ?? ' '}</div>
          <ShowIf condition={!!subtitle}>
            <div className="small text-body-secondary text-truncate">{subtitle}</div>
          </ShowIf>
        </div>
      </ShowIf>
      <div>
        {map(rows, (row) => (
          <LabelValueBlock key={row.label} className="py-1" label={row.label} value={row.value ?? '---'} />
        ))}
      </div>
      <ShowIf condition={!!list && (loading || !isEmpty(list?.items))}>
        <div className="small">
          <div className="fw-bold">
            {list?.label}
            <ShowIf condition={size(list?.items) > 1}> ({size(list?.items)})</ShowIf>
          </div>
          {map(take(list?.items, ENTITY_PREVIEW_LIST_LIMIT), (item, index) => (
            <div key={index} className="text-truncate">
              {item}
            </div>
          ))}
          <ShowIf condition={size(list?.items) > ENTITY_PREVIEW_LIST_LIMIT}>
            <div className="text-body-secondary">+{size(list?.items) - ENTITY_PREVIEW_LIST_LIMIT} more</div>
          </ShowIf>
        </div>
      </ShowIf>
    </Stack>
  </LoadingOverlay>
)
